import * as React from 'react';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { AnchorListItem } from './AnchorListItem';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { DeepReadonly } from 'utility-types';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { fetchClientOverridesCount } from '@pec/aion-ui-core/actions/fetchClientOverridesCount';
import { fetchClientProcesses } from '@pec/aion-ui-core/slices/clientProcesses';
import { InlineBadge } from '../InlineBadge';
import { ListSubheader } from './ListSubheader';
import { NavLinkListItem } from './NavLinkListItem';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { ProcessType } from '@pec/aion-ui-core/interfaces/process';
import { SectionContainer } from '../../containers/Nav/Section';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const { AionSafetyProgramsUI, AionScoringUI } = AppName;

export type Props = {
  hasGlobalPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasOrganizationPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  features: DeepReadonly<OrganizationFeature[]>;
  organizationId: string;
  pendingReleasesCount: number | null;
};

export const ContractorManagementSectionComponent: React.FC<Props> = ({
  hasGlobalPermission,
  hasOrganizationPermission,
  features,
  organizationId,
  pendingReleasesCount
}) => {
  const { Read, Write, Manage, Audit } = ActivityAction;
  const {
    BusinessUnits: BusinessUnitsFeature,
    PreQualificationGroups: PreQualificationGroupsFeature,
    ActivityFeed: ActivityFeedFeature,
    QuestionnaireV2: QuestionnaireV2Feature,
    ProcessAutomation: ProcessAutomationFeature
  } = OrganizationFeature;
  const {
    Organizations,
    Releases,
    BusinessUnits,
    ScoreSets,
    Scoring,
    SafetyProgramAnswers,
    SafetyProgramEvaluations,
    SafetyProgramRequirementClients,
    PreQualificationGroups,
    ActivityFeed,
    ProcessAutomation
  } = ActivityResourceName;

  const isAdmin = hasGlobalPermission(Manage, Organizations);

  const { clientOverridesCount } = useSelector((state: CommonRootState) => state.clientOverridesCount);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { clientProcesses } = useSelector((state: CommonRootState) => state.clientProcesses);

  const hasSafetyProgramRequirementClientsWritePermission =
    hasGlobalPermission(Write, SafetyProgramRequirementClients) ||
    hasOrganizationPermission(Write, SafetyProgramRequirementClients);

  const hasSafetyProgramRequirementClientsManagePermission =
    hasGlobalPermission(Manage, SafetyProgramRequirementClients) ||
    hasOrganizationPermission(Manage, SafetyProgramRequirementClients);

  const hasSafetyProgramRequirementClientsAuditPermission =
    hasGlobalPermission(Audit, SafetyProgramRequirementClients) ||
    hasOrganizationPermission(Audit, SafetyProgramRequirementClients);

  const hasScoreSetsManagePermission =
    hasGlobalPermission(Manage, ScoreSets) || hasOrganizationPermission(Manage, ScoreSets);

  const hasReadPermission =
    !hasSafetyProgramRequirementClientsWritePermission &&
    !hasSafetyProgramRequirementClientsManagePermission &&
    !hasSafetyProgramRequirementClientsAuditPermission;

  useEffect(() => {
    dispatch(fetchClientOverridesCount(organizationId));
  }, [dispatch, organizationId]);

  useEffect(() => {
    if (features.includes(ProcessAutomationFeature)) {
      dispatch(fetchClientProcesses({ organizationId }));
    }
  }, [ProcessAutomationFeature, dispatch, features, organizationId]);

  const activeAddContractorProcess = useMemo(
    () =>
      features.includes(ProcessAutomationFeature) && clientProcesses && Array.isArray(clientProcesses)
        ? clientProcesses.find(process => Number(process.type) === ProcessType.AddContractor && process.isActive)
        : null,
    [ProcessAutomationFeature, clientProcesses, features]
  );

  const renderSafetyProgramsLinkContent = () => clientOverridesCount && clientOverridesCount > 0 && !hasReadPermission;

  const shouldShowActivityFeedLink =
    features.includes(ActivityFeedFeature) &&
    (hasOrganizationPermission(Read, ActivityFeed) || hasGlobalPermission(Read, ActivityFeed));

  return (
    <SectionContainer
      id="ContractorManagement"
      subheader={t('components.contractorManagementSection.contractorManagement', 'Contractor Management')}
      icon={<SupervisorAccountIcon />}
      hasNotifications={Boolean(pendingReleasesCount)}
    >
      <FeatureFlagWrapper>
        <Feature name="AionPreQualificationGroupsUI">
          {features.includes(PreQualificationGroupsFeature) && hasOrganizationPermission(Read, PreQualificationGroups) && (
            <AnchorListItem inset href={`/organizations/${organizationId}/supply-chains`}>
              {t('components.drawerLinks.supplyChains', 'Supply Chains')}
            </AnchorListItem>
          )}
        </Feature>
      </FeatureFlagWrapper>
      {shouldShowActivityFeedLink && (
        <AnchorListItem inset href={`/organizations/${organizationId}/activity-feed`}>
          {t('components.contractorManagementSection.activityFeed', 'Activity Feed')}
        </AnchorListItem>
      )}
      {features.includes(BusinessUnitsFeature) &&
        hasOrganizationPermission(Manage, BusinessUnits) &&
        (!features.includes(QuestionnaireV2Feature) || isAdmin) && (
          <AnchorListItem inset href="/SSQV4/SSQV5/Operator/BUAdmin">
            {t('components.contractorManagementSection.businessUnits', 'Business Units')}
          </AnchorListItem>
        )}
      {features.includes(QuestionnaireV2Feature) &&
        hasScoreSetsManagePermission &&
        (process.env.REACT_APP_NAME === AionScoringUI ? (
          <NavLinkListItem inset to={`/${organizationId}/scoring/sets`}>
            {t('components.contractorManagementSection.scoring', 'Scoring')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/scoring/sets`}>
            {t('components.contractorManagementSection.scoring', 'Scoring')}
          </AnchorListItem>
        ))}
      {hasOrganizationPermission(Manage, Scoring) && (
        <React.Fragment>
          <ListSubheader inset>
            {t('components.contractorManagementSection.configureScorecards', 'Configure Scorecards')}
          </ListSubheader>
          <AnchorListItem inset href="/SSQV4/Deficiency/BUDeficiencyTree.aspx">
            {t('components.contractorManagementSection.buDeficiencies', 'BU Deficiencies')}
          </AnchorListItem>
          <AnchorListItem inset href="/SSQV4/Requirements/BURequirementsTree.aspx">
            {t('components.contractorManagementSection.buMinimumStandards', 'BU Minimum Standards')}
          </AnchorListItem>
          {features.includes(BusinessUnitsFeature) && (
            <AnchorListItem inset href="/SSQV4/Scoring/BUScoringTree.aspx">
              {t('components.contractorManagementSection.buScoring', 'BU Scoring')}
            </AnchorListItem>
          )}
          <AnchorListItem inset href="/SSQV4/Requirements/RequirementTree.aspx">
            {t('components.contractorManagementSection.minimumStandards', 'Minimum Standards')}
          </AnchorListItem>
          <AnchorListItem inset href="/SSQV4/Deficiency/DeficiencyTree.aspx">
            {t('components.contractorManagementSection.ssqDeficiencies', 'SSQ Deficiencies')}
          </AnchorListItem>
          <AnchorListItem inset href="/SSQV4/Scoring/ScoringTree.aspx">
            {t('components.contractorManagementSection.ssqScoring', 'SSQ Scoring')}
          </AnchorListItem>
        </React.Fragment>
      )}
      {hasOrganizationPermission(Read, Organizations) && (
        <AnchorListItem inset href="/SSQV4/SSQV5/Operator/ContractorArchive">
          {t('components.contractorManagementSection.contractorArchive', 'Contractor Archive')}
        </AnchorListItem>
      )}
      {hasOrganizationPermission(Manage, Releases) && (
        <AnchorListItem inset href="/SSQV4/SSQV5/Contractor/ManageReleases">
          <InlineBadge id="pending-release-count" badgeContent={pendingReleasesCount}>
            {t('components.contractorManagementSection.pendingConnections', 'Pending Connections')}
          </InlineBadge>
        </AnchorListItem>
      )}
      {features.includes(ProcessAutomationFeature) &&
        (hasOrganizationPermission(Read, ProcessAutomation) || hasGlobalPermission(Read, ProcessAutomation)) &&
        (process.env.REACT_APP_NAME === AppName.AionProcessAutomationUI ? (
          <NavLinkListItem inset to={`/organizations/${organizationId}/process-automation`}>
            {t('components.contractorManagementSection.processAutomation', 'Process Automation')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/process-automation`}>
            {t('components.contractorManagementSection.processAutomation', 'Process Automation')}
          </AnchorListItem>
        ))}
      {(hasOrganizationPermission(Read, SafetyProgramRequirementClients) ||
        hasGlobalPermission(Read, SafetyProgramAnswers) ||
        hasGlobalPermission(Manage, SafetyProgramEvaluations)) &&
        (process.env.REACT_APP_NAME === AionSafetyProgramsUI ? (
          <NavLinkListItem inset to={`/organizations/${organizationId}/safety-program-requirements`}>
            {renderSafetyProgramsLinkContent() ? (
              <InlineBadge badgeContent={clientOverridesCount}>
                {t('components.contractorManagementSection.programReviews', 'Program Reviews')}
              </InlineBadge>
            ) : (
              t('components.contractorManagementSection.programReviews', 'Program Reviews')
            )}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/safety-program-requirements`}>
            {renderSafetyProgramsLinkContent() ? (
              <InlineBadge badgeContent={clientOverridesCount}>
                {t('components.contractorManagementSection.programReviews', 'Program Reviews')}
              </InlineBadge>
            ) : (
              t('components.contractorManagementSection.programReviews', 'Program Reviews')
            )}
          </AnchorListItem>
        ))}
      {hasOrganizationPermission(Read, Organizations) && (
        <AnchorListItem inset href="/SSQV4/SSQV5/Operator/ContractorTags">
          {t('components.contractorManagementSection.tags', 'Tags')}
        </AnchorListItem>
      )}
      {hasOrganizationPermission(Manage, Organizations) && (
        <AnchorListItem
          inset
          href="/SSQV4/Admin/TrainingStandards/TrainingStandardListing.aspx?TrainingStandardOperation=true"
        >
          {t('components.contractorManagementSection.trainingStandards', 'Training Standards')}
        </AnchorListItem>
      )}
      {hasOrganizationPermission(Write, Organizations) &&
        (features.includes(ProcessAutomationFeature) && activeAddContractorProcess ? (
          <AnchorListItem
            inset
            href={`/organizations/${organizationId}/process-automation/${activeAddContractorProcess.id}/form/${activeAddContractorProcess.formId}`}
          >
            {t('components.contractorManagementSection.requestNewContractor', 'Request New Contractor')}
          </AnchorListItem>
        ) : (
          <AnchorListItem inset href="/SSQV4/SSQV5/Contractor/RequestNewContractor">
            {t('components.contractorManagementSection.requestNewContractor', 'Request New Contractor')}
          </AnchorListItem>
        ))}
    </SectionContainer>
  );
};
